import { useContext, useMemo } from 'react'

import { MobXProviderContext } from 'mobx-react'

import { PropertyType } from '~/client/graph/types.generated'
import {
  ICardConfiguration,
  IResourceProperty,
} from '~/client/src/shared/components/ResourceCardList/types'
import { useTagsStore } from '~/client/src/shared/hooks/useTagsStore'
import { getResourceDefinition } from '~/client/src/shared/resources/definitions'
import { IResourceRuntimeContext } from '~/client/src/shared/resources/definitions/types'
import type {
  ResourcePropertyDto,
  ResourceTemplateDto,
} from '~/client/src/shared/resources/domain/resourceSchema'
import { getDefaultCardTemplate } from '~/client/src/shared/resources/domain/resourceSchema'
import {
  buildResourceCardConfiguration,
  USE_DEFAULT_CARD_CONFIG,
} from '~/client/src/shared/resources/presentation/buildResourceCardConfiguration'
import { createResourceDisplayValue } from '~/client/src/shared/resources/presentation/createResourceDisplayValue'

export interface ResourceCardSchema {
  readonly templates: ResourceTemplateDto[]
  readonly properties: ResourcePropertyDto[]
  readonly loading: boolean
  readonly error: string | null
  readonly isReady: boolean
  // Readiness of the properties half alone. Optional so providers that fetch their own
  // schema need not supply it; those fall back to the combined fields below.
  readonly propertiesLoading?: boolean
  readonly propertiesError?: string | null
  readonly propertiesReady?: boolean
}

/**
 * Readiness of a prefetched schema's properties half.
 *
 * The properties-only fields are optional, so a provider that does not supply them falls
 * back to the combined ones — but only when a field is genuinely ABSENT. `??` would also
 * fall back on `null`, and `propertiesError: null` is the success case: properties loaded
 * cleanly. Falling back there would pick up `error`, which a templates-only failure sets,
 * and report properties not ready exactly when they are fine.
 */
export function resolvePrefetchedReadiness(schema: ResourceCardSchema): {
  propertiesReady: boolean
  ready: boolean
} {
  const propertiesError =
    schema.propertiesError === undefined ? schema.error : schema.propertiesError
  const propertiesReady =
    schema.propertiesReady === undefined
      ? schema.isReady
      : schema.propertiesReady
  const propertiesLoading =
    schema.propertiesLoading === undefined
      ? schema.loading
      : schema.propertiesLoading

  return {
    propertiesReady: propertiesReady && !propertiesError,
    ready: !propertiesLoading,
  }
}

interface UseResourceCardSchemaParams {
  resourceTypeId: string
  runtimeContext: IResourceRuntimeContext
  schema: ResourceCardSchema
}

export function useResourceCardSchema({
  resourceTypeId,
  runtimeContext,
  schema,
}: UseResourceCardSchemaParams) {
  // User and Company properties resolve their ids to names through these; without them a
  // card shows a raw ObjectId and "Unknown User".
  const { projectDateStore, projectMembersStore, companiesStore } =
    useContext(MobXProviderContext)
  const tagsStore = useTagsStore()

  const resourceDefinition = getResourceDefinition(resourceTypeId)
  const propertyDefinitions = schema.properties

  const template = USE_DEFAULT_CARD_CONFIG
    ? undefined
    : getDefaultCardTemplate(schema.templates)

  const cardConfiguration: ICardConfiguration = useMemo(
    () => buildResourceCardConfiguration(resourceDefinition, template),
    [resourceDefinition, template?.cardConfiguration],
  )

  const properties: IResourceProperty[] = useMemo(() => {
    return propertyDefinitions.map(prop => ({
      name: prop.name,
      displayName: prop.displayName,
      propertyType: prop.propertyType as PropertyType,
      configuration: prop.configuration
        ? {
            multiline: prop.configuration.multiline || undefined,
            options: prop.configuration.options?.map(opt => ({
              value: opt.value,
              label: opt.label,
              parentValue: opt.parentValue ?? undefined,
            })),
            format: prop.configuration.format || undefined,
            unit: prop.configuration.unit || undefined,
            resourceLink: prop.configuration.resourceLink
              ? {
                  targetResourceType:
                    prop.configuration.resourceLink.targetResourceType,
                }
              : undefined,
          }
        : undefined,
    }))
  }, [propertyDefinitions])

  const getDisplayValue = useMemo(
    () =>
      createResourceDisplayValue({
        properties,
        resourceTypeId,
        runtimeContext,
        projectDateStore,
        projectMembersStore,
        companiesStore,
        tagsStore,
      }),
    [
      properties,
      resourceTypeId,
      runtimeContext,
      projectDateStore,
      projectMembersStore,
      companiesStore,
      tagsStore,
    ],
  )

  return {
    cardConfiguration,
    properties,
    getDisplayValue,
    ...resolvePrefetchedReadiness(schema),
  }
}
